import { useMemo } from 'react';
import { useTheme } from 'styled-components';

import { Typography } from '@libs/components';
import { HistoryPeriodEnumKeys, RealFiatAssets } from '@libs/types';
import { screenSizes } from '@libs/theme';

import { CurrencySymbol } from 'src/constants';
import { useDimensions, useTranslation, WalletChartInfo } from 'src/hooks';
import { ChartAssetCardsLoader, PieChartLoader } from 'src/content-loaders';
import { DesertedIcon } from 'src/assets';
import { DataWithLoader } from 'src/components';

import PieChartDetails from '../pie-chart-details/PieChartDetails';
import PieChartOtherData from '../pie-chart-other-data/PieChartOtherData';
import { DonutChartData, PieChart } from '../pie-chart/PieChart';
import SelectedChartItemDetails, {
  SelectedChartItemDetailsProps,
} from '../selected-chart-item-details/SelectedChartItemDetails';

import {
  Chart,
  PieChartWrapper,
  RelativeWrapper,
  SelectedChartItemDetailsWrapper,
  TextContainer,
} from './styles';

export const PIE_CHART_PERCENTAGE_THRESHOLD = 0.5;
export enum OtherChart {
  OTHER = 'OTHER',
}

type PieChartDataProps = SelectedChartItemDetailsProps & {
  activeDurationKey: HistoryPeriodEnumKeys;
  selectedFiat: RealFiatAssets;
  activeIndex: number | OtherChart.OTHER | undefined;
  setActiveIndex: (index: number | OtherChart.OTHER | undefined) => void;
  otherActiveIndex: number | undefined;
  setOtherActiveIndex: (index: number | undefined) => void;
  isSelectedAssetAveragePriceVisible: boolean;
  walletChartInfo: WalletChartInfo;
};
const PieChartData: React.FC<PieChartDataProps> = ({
  activeDurationKey,
  selectedFiat,
  activeIndex,
  setActiveIndex,
  otherActiveIndex,
  setOtherActiveIndex,
  isSelectedAssetAveragePriceVisible,
  walletChartInfo,
  ...rest
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { width } = useDimensions();

  const OTHER_COLOR = colors.borders.pressed;

  const currencySymbol = CurrencySymbol[selectedFiat];

  const {
    filtered,
    filteredOut,
    totalAssetCount,
    otherCoinsPercentage,
    otherCoinsValue,
    loading,
  } = walletChartInfo;

  const otherData: DonutChartData = useMemo(() => {
    const percentageToApply = Math.max(
      otherCoinsPercentage,
      PIE_CHART_PERCENTAGE_THRESHOLD
    );

    return {
      key: OtherChart.OTHER.toString(),
      value: percentageToApply,
      color: OTHER_COLOR,
      isActive: activeIndex === OtherChart.OTHER,
      onPress: setActiveIndex.bind(null, OtherChart.OTHER),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherCoinsPercentage, activeIndex]);

  const chartData: DonutChartData[] = useMemo(() => {
    if (!filtered.length) {
      return [
        {
          key: OtherChart.OTHER.toString(),
          value: 100,
          color: colors.background.quaternary,
          disabled: true,
        },
      ];
    }

    const filteredArr: DonutChartData[] = filtered.map((item, index) => {
      return {
        key: item.code.toString(),
        value: item.percentage || 0,
        isActive: activeIndex === index,
        color: item.color || '',
      };
    });

    if (filteredOut.length > 0) {
      filteredArr.push(otherData);
    }

    return filteredArr;
  }, [filtered, otherData, filteredOut, colors, activeIndex]);

  const isOtherChartActive = activeIndex === OtherChart.OTHER;
  const selectedAsset =
    typeof activeIndex === 'number' ? filtered[activeIndex] : undefined;

  const titleType = width < screenSizes.laptop ? 'bodyButton' : 'subHeadline';

  const shouldShowAssetCardLoader = width < screenSizes.laptop;

  return (
    <RelativeWrapper>
      <div>
        {!isOtherChartActive && (
          <TextContainer>
            <Typography
              type={titleType}
              color={colors.text.secondary}
              fontFamily="primaryMedium"
              fontWeight={700}
            >
              {!selectedAsset
                ? t('wallet_pie_title')
                : t('wallet_my_asset_data', { name: selectedAsset.name })}
            </Typography>
          </TextContainer>
        )}

        <DataWithLoader
          loader={<PieChartLoader />}
          loading={loading}
          emptyStateTitle={t('error_asset_not_found')}
          icon={DesertedIcon}
          hasData={!!filtered.length}
        >
          <Chart>
            {!isOtherChartActive ? (
              <PieChartWrapper>
                <PieChart
                  data={chartData}
                  onChartItemPress={setActiveIndex}
                  hasActiveSelection={activeIndex !== undefined}
                >
                  <PieChartDetails
                    totalAssets={totalAssetCount}
                    selectedAsset={selectedAsset}
                    currencySymbol={currencySymbol}
                    activeDurationKey={activeDurationKey}
                  />
                </PieChart>
              </PieChartWrapper>
            ) : (
              <PieChartOtherData
                data={filteredOut}
                currencySymbol={currencySymbol}
                activeDurationKey={activeDurationKey}
                totalValue={otherCoinsValue}
                activeIndex={otherActiveIndex}
                setActiveIndex={setActiveIndex}
                setOtherActiveIndex={setOtherActiveIndex}
              />
            )}
          </Chart>
        </DataWithLoader>
      </div>
      <>{loading && shouldShowAssetCardLoader && <ChartAssetCardsLoader />}</>

      {isSelectedAssetAveragePriceVisible && (
        <SelectedChartItemDetailsWrapper>
          <SelectedChartItemDetails {...rest} />
        </SelectedChartItemDetailsWrapper>
      )}
    </RelativeWrapper>
  );
};

export default PieChartData;
